import { useEffect, useRef, useState } from 'react';
import Button from 'src/componentes/button';
import Dialog from 'src/componentes/dialog';
import ButtonToolbar from 'src/componentes/button-toolbar';
import { Row, Col } from 'src/componentes/grid';
import useAuth from 'src/hooks/useAuth';
import { useIntl } from 'react-intl';
import Input from 'src/componentes/input';
import errorHandler from 'src/utils/error-handler';
import http from 'src/services/httpService';
import LoadingContainer from 'src/componentes/loading-container';
import DicionarioRiscoLookup from 'src/lookups/DicionarioRiscos/DicionarioRiscoLookup';

function DicionarioRiscoEdit({
  item,
  indexItem,
  endpoint,
  parameters,
  handleClose,
  disabled,
  refreshData,
  ...rest
}) {
  const defaultItem = {
    periodicidade: null,
    dataInicio: null,
    dataTermino: null,
    utilizarRecorrencia: false,
    excluido: false,
    editado: true,
    permissions: {
      allowEdit: true,
      allowDelete: true,
      allowView: true,
    },
  };

  const intl = useIntl();
  const modalRef = useRef();
  const { terms: resources } = useAuth();
  const [isNew] = useState(item == null ? true : false);
  const [model, setModel] = useState(item == null ? defaultItem : item);
  const [modelErrors, setModelErrors] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleCurrentClose = (isSaved = false) => {
    if (modalRef.current) modalRef.current.close();

    setTimeout(() => {
      handleClose && handleClose(isSaved, isNew, model, indexItem);
      if (isSaved) refreshData && refreshData();
    }, 300);
  };

  const handleCloseClick = (isSaved = true) => {
    handleCurrentClose(isSaved);
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      if (item && item.id) {
        const response = await http.post(`${endpoint}/Edit`, {
          id: item.id,
          parameters: parameters,
        });
        setModel(response.data);
      } else {
        const response = await http.post(`${endpoint}/Create`, {
          parameters: parameters,
        });
        setModel(response.data);
      }
    } catch (err) {
      errorHandler(err);
      handleCloseClick && handleCloseClick();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const validadeField = () => {
    const modelValidate = {};
    if (!model.nome) modelValidate.nome = true;
    if (!model.idIntegracao) modelValidate.idIntegracao = true;

    if (modelValidate.nome || modelValidate.idIntegracao) {
      setModelErrors(modelValidate);
      return false;
    }
    return true;
  };

  const handleSaveClick = async () => {
    try {
      setIsLoading(true);

      if (validadeField()) {
        await http
          .post(`${endpoint}/Save`, {
            model: { ...model },
            parameters: parameters,
          })
          .then(() => {
            handleCurrentClose(true);
          })
          .catch((err) => {
            errorHandler(err);
          });
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeValue = (field, value) => {
    setModel((current) => ({ ...current, [field]: value }));
  };

  const isDisabled =
    isLoading ||
    disabled ||
    (model && model.permissions && !model.permissions.allowEdit);

  const editActions = [
    <ButtonToolbar key="toolbar-tela">
      <Button onClick={() => handleCloseClick(false)} color="secondary">
        {intl.formatMessage({ id: 'label.cancelar' })}
      </Button>
      <Button
        type="primary"
        onClick={handleSaveClick}
        htmlType="submit"
        disabled={isDisabled}
      >
        {intl.formatMessage({ id: 'label.salvar' })}
      </Button>
    </ButtonToolbar>,
  ];

  const renderTitle = () => {
    return intl.formatMessage(
      { id: 'labelDicionarioRiscos' },
      { riscos: resources.riscos }
    );
  };

  return (
    <Dialog
      id="recorrencia-edit"
      ref={modalRef}
      width="600px"
      minHeight="50%"
      title={renderTitle()}
      padContent
      actions={editActions}
      {...rest}
    >
      <LoadingContainer isLoading={isLoading}>
        <Row>
          <Col md={12}>
            <Input
              disabled={isDisabled}
              maxLength={300}
              model={{
                label: intl.formatMessage({ id: 'idIntegracao' }),
                value: model.idIntegracao,
                requestChange: (e) => handleChangeValue('idIntegracao', e),
                errors: modelErrors && modelErrors.idIntegracao,
              }}
              required
            />
          </Col>
          <Col md={12}>
            <Input
              maxLength={1000}
              disabled={isDisabled}
              model={{
                label: intl.formatMessage({ id: 'nome' }),
                value: model.nome,
                requestChange: (e) => handleChangeValue('nome', e),
                errors: modelErrors && modelErrors.nome,
              }}
              required
            />
          </Col>
          <Col md={12}>
            <DicionarioRiscoLookup
              disabled={isDisabled}
              model={{
                label: intl.formatMessage({ id: 'labelCategoriaSuperior' }),
                value: model.registroPai,
                requestChange: (e) => handleChangeValue('registroPai', e),
                errors: modelErrors && modelErrors.registroPai,
              }}
              data={{ excludeId: model.id, ignorarUltimoNivel: true }}
            />
          </Col>
          <Col md={12}>
            <Input
              maxLength={1000}
              rows={5}
              type="textarea"
              disabled={isDisabled}
              model={{
                label: intl.formatMessage({ id: 'descricao' }),
                value: model.descricao,
                requestChange: (e) => handleChangeValue('descricao', e),
              }}
            />
          </Col>
        </Row>
      </LoadingContainer>
    </Dialog>
  );
}

export default DicionarioRiscoEdit;
